<template>
    <div v-if="shouldShowLauncher" class="floating-ai-chat-launcher fixed right-4 z-30 md:right-5" :style="wrapperStyle">
        <transition mode="out-in" enter-active-class="transition duration-200 ease-out"
            enter-class="translate-y-3 opacity-0" enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in" leave-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-3 opacity-0">
            <section v-if="showPanel" key="panel"
                class="mb-3 flex h-[min(88vh,920px)] w-[min(396px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-[30px] border border-base-300 bg-gradient-to-b from-base-100 via-base-100 to-base-200/40 shadow-2xl md:w-[396px]">
                <div class="relative flex min-h-0 flex-1 overflow-hidden">
                    <transition enter-active-class="transition duration-200 ease-out"
                        enter-class="-translate-x-4 opacity-0" enter-to-class="translate-x-0 opacity-100"
                        leave-active-class="transition duration-150 ease-in" leave-class="translate-x-0 opacity-100"
                        leave-to-class="-translate-x-4 opacity-0">
                        <aside v-if="showSessions"
                            class="absolute inset-y-0 left-0 z-20 w-[196px] border-r border-base-300 bg-base-100/95 shadow-xl backdrop-blur md:w-[212px]">
                            <div class="flex h-full flex-col p-3">
                                <div class="mb-3 flex items-center justify-between gap-2">
                                    <div class="text-xs font-medium uppercase tracking-[0.18em] text-base-content/45">会话
                                    </div>
                                    <button type="button" class="btn btn-ghost btn-xs btn-circle"
                                        @click="showSessions = false">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M6 6l12 12M18 6L6 18" />
                                        </svg>
                                    </button>
                                </div>

                                <button type="button" class="btn btn-primary btn-sm mb-3 w-full"
                                    :disabled="chat.isCurrentSessionLoading" @click="startNewDraft">
                                    新建对话
                                </button>

                                <div class="min-h-0 flex-1 overflow-y-auto pr-1">
                                    <button v-for="session in chat.sessionList" :key="session.id" type="button"
                                        class="mb-2 block w-full rounded-2xl border px-3 py-3 text-left transition duration-200"
                                        :class="chat.activeSessionId === session.id ? 'border-primary bg-primary text-primary-content shadow-lg shadow-primary/15' : 'border-base-300 bg-base-100/90 text-base-content shadow-sm hover:-translate-y-0.5 hover:bg-base-100 hover:shadow-md'"
                                        @click="selectSession(session.id)">
                                        <div class="line-clamp-2 text-xs font-medium leading-5">
                                            {{ chat.getSessionTitle(session) }}
                                        </div>
                                        <div
                                            class="mt-2 flex items-center justify-between gap-2 text-[11px] opacity-70">
                                            <span class="truncate">{{ shortSessionId(session.id) }}</span>
                                            <button type="button" class="link link-hover"
                                                @click.stop="deleteSession(session.id)">
                                                删除
                                            </button>
                                        </div>
                                    </button>

                                    <div v-if="!chat.sessionList.length"
                                        class="rounded-2xl border border-dashed border-base-300 px-3 py-8 text-center text-xs text-base-content/60">
                                        先发一条消息，这里会保存你的会话。
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </transition>

                    <button v-if="showSessions" type="button" class="absolute inset-0 z-10 bg-base-content/5"
                        @click="showSessions = false">
                    </button>

                    <div class="flex min-w-0 flex-1 flex-col bg-base-100">
                        <div
                            class="flex items-center justify-between gap-2 border-b border-base-300 bg-base-100/70 px-4 py-2.5 text-xs text-base-content/60 backdrop-blur md:px-5">
                            <div class="flex items-center gap-2">
                                <button type="button" class="btn btn-ghost btn-xs btn-circle"
                                    @click="showSessions = true">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M4 7h16M4 12h12M4 17h9" />
                                    </svg>
                                </button>
                                <span
                                    class="rounded-full bg-base-200 px-2.5 py-1 text-[11px] font-medium text-base-content/75">{{
                                    activeSessionLabel }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span v-if="chat.isCurrentSessionLoading"
                                    class="badge badge-warning badge-sm">生成中</span>
                                <button type="button" class="btn btn-ghost btn-xs" :disabled="!chat.activeSessionId"
                                    @click="reloadMessages">
                                    刷新
                                </button>
                                <button type="button" class="btn btn-ghost btn-xs btn-circle" @click="closePanel">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div ref="messageScroller"
                            class="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-base-200/10 via-base-200/30 to-base-100 px-4 py-5 md:px-5">
                            <div class="space-y-4">
                                <article v-for="(message, index) in chat.messages" :key="message.messageId || index"
                                    class="flex" :class="message.role === 'user' ? 'justify-end' : 'justify-start'">
                                    <div class="max-w-[90%] md:max-w-[84%]">
                                        <div class="mb-1.5 px-1 text-[11px] text-base-content/45"
                                            :class="message.role === 'user' ? 'text-right' : 'text-left'">
                                            {{ message.role === 'user' ? '你' : '助手' }}
                                        </div>
                                        <div class="min-w-0 overflow-hidden px-4 py-3.5 text-sm leading-7 transition"
                                            :class="message.role === 'user' ? 'rounded-[24px] rounded-br-lg bg-primary text-primary-content shadow-lg shadow-primary/15' : 'rounded-[24px] rounded-bl-lg border border-base-300 bg-base-100/95 text-base-content shadow-md'">
                                            <div v-if="message.reasoning"
                                                class="mb-3 overflow-hidden rounded-2xl border border-base-300/80 bg-base-200/80 text-xs leading-6 text-base-content/70"
                                                :class="isReasoningStreaming(message, index) ? 'ring-1 ring-primary/20' : ''">
                                                <button type="button"
                                                    class="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-base-100/40"
                                                    @click="toggleReasoning(message, index)">
                                                    <span
                                                        class="inline-flex items-center gap-2 rounded-full bg-base-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-base-content/60">
                                                        <span>思考过程</span>
                                                        <span v-if="isReasoningStreaming(message, index)"
                                                            class="inline-flex items-center gap-1 text-primary">
                                                            <span
                                                                class="h-1.5 w-1.5 animate-pulse rounded-full bg-current"></span>
                                                            <span class="loading loading-dots loading-xs"></span>
                                                        </span>
                                                    </span>
                                                    <svg xmlns="http://www.w3.org/2000/svg"
                                                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                                                        :class="isReasoningExpanded(message, index) ? 'rotate-180' : ''"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            d="M6 9l6 6 6-6" />
                                                    </svg>
                                                </button>
                                                <div v-if="isReasoningExpanded(message, index)"
                                                    class="border-t border-base-300/70 px-3 py-2.5"
                                                    v-html="chat.renderMarkdownHtml(message.reasoning)"></div>
                                            </div>
                                            <div v-if="hasVisibleProcessParts(message)" class="mb-3 space-y-2 text-xs leading-6 text-base-content">
                                                <div class="px-1 text-[11px] font-semibold tracking-wide text-base-content/60">
                                                    执行流
                                                </div>
                                                <div v-for="(part, partIndex) in getVisibleProcessParts(message)" :key="getProcessPartKey(message, index, part, partIndex)" class="overflow-hidden rounded-2xl border border-base-300/80 bg-base-100">
                                                    <button type="button" class="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-base-200/40" @click="toggleProcessPart(message, index, part, partIndex)">
                                                        <span class="min-w-0">
                                                            <span class="block truncate text-[11px] font-semibold tracking-wide text-base-content/75">
                                                                {{ getProcessPartLabel(part) }}
                                                            </span>
                                                            <span class="mt-0.5 block truncate text-[11px] text-base-content/55">
                                                                {{ getProcessPartPreview(part) }}
                                                            </span>
                                                        </span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 transition-transform duration-200 text-base-content/45" :class="isProcessPartExpanded(message, index, part, partIndex) ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    </button>
                                                    <div v-if="isProcessPartExpanded(message, index, part, partIndex)" class="border-t border-base-300/70 px-3 py-2.5">
                                                        <pre class="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-6 text-base-content/80">{{ getProcessPartSummary(part, false) }}</pre>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="message.role === 'assistant'"
                                                class="prose prose-sm max-w-none break-words text-inherit"
                                                v-html="chat.renderMarkdownHtml(message.content)">
                                            </div>
                                            <div v-else class="whitespace-pre-wrap break-words">
                                                {{ message.content }}
                                            </div>
                                        </div>
                                    </div>
                                </article>

                                <div v-if="chat.isCurrentSessionLoading && !chat.hasCurrentSessionReplyContent"
                                    class="flex justify-start">
                                    <div
                                        class="rounded-[22px] rounded-bl-lg border border-base-300 bg-base-100/95 px-4 py-3 text-sm text-base-content shadow-md">
                                        <span class="loading loading-dots loading-sm"></span>
                                    </div>
                                </div>

                                <div v-if="!chat.messages.length"
                                    class="rounded-[28px] border border-dashed border-base-300 bg-gradient-to-b from-base-100 to-base-200/40 px-6 py-10 text-center text-sm text-base-content/60 shadow-md">
                                    <div
                                        class="mx-auto flex h-14 w-14 items-center justify-center rounded-[18px] bg-primary text-primary-content shadow-lg shadow-primary/15 ring-1 ring-primary/10">
                                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                                            class="launcher-icon h-8 w-8 shrink-0" fill="currentColor">
                                            <path
                                                d="M12 2.75L13.817 8.183L19.25 10L13.817 11.817L12 17.25L10.183 11.817L4.75 10L10.183 8.183L12 2.75Z"
                                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                            <path
                                                d="M18.5 15.5L19.232 17.518L21.25 18.25L19.232 18.982L18.5 21L17.768 18.982L15.75 18.25L17.768 17.518L18.5 15.5Z"
                                                fill="currentColor" />
                                        </svg>
                                    </div>
                                    <div class="mt-4 font-medium text-base-content">欢迎使用智能助手</div>
                                    <div class="mt-2">我是 Music Tag Web 智能助手, 我可以帮你快速了解和使用本项目。</div>

                                    <div class="mt-6 grid gap-2.5 text-left sm:grid-cols-2">
                                        <button v-for="prompt in quickPrompts" :key="prompt" type="button"
                                            class="rounded-2xl border border-base-300 bg-base-100/90 px-4 py-3.5 text-xs leading-5 text-base-content shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-base-100 hover:shadow-md"
                                            @click="applyQuickPrompt(prompt)">
                                            {{ prompt }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <footer class="border-t border-base-300 bg-base-100/90 px-4 py-3 backdrop-blur md:px-5">
                            <div
                                class="rounded-[24px] border border-base-300 bg-base-100/95 p-2 shadow-sm ring-1 ring-base-200/70">
                                <textarea v-model="input" rows="3"
                                    class="textarea h-20 w-full resize-none border-0 bg-transparent p-2 text-sm leading-6 focus:outline-none"
                                    placeholder="请输入你想让 AI 帮你处理的问题..." @keydown.enter.exact.prevent="sendMessage">
                                </textarea>
                                <div class="mt-1.5 flex items-center justify-between gap-3 px-2 pb-0.5">
                                    <div class="text-[11px] text-base-content/50">Enter 发送，Shift + Enter 换行</div>
                                    <div class="flex items-center gap-2">
                                        <button type="button" class="btn btn-ghost btn-sm rounded-full"
                                            :disabled="!chat.isCurrentSessionLoading" @click="abort">
                                            停止
                                        </button>
                                        <button type="button"
                                            class="btn btn-primary btn-sm rounded-full shadow-md shadow-primary/15"
                                            :disabled="chat.isCurrentSessionLoading" @click="sendMessage">
                                            发送
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                </div>
            </section>
            <button v-else key="trigger" type="button"
                class="launcher-trigger btn btn-circle btn-ghost h-14 w-14 items-center justify-center border border-base-200 bg-base-100/80 p-0 text-primary-600 shadow-2xl backdrop-blur md:h-[60px] md:w-[60px]"
                :class="{ 'cursor-grabbing': isDragging }"
                :title="launcherTitle" @click="openPanel" @mousedown.prevent="onDragStart" @touchstart.prevent="onDragStart">
                <span v-if="!isLauncherAvailable" class="launcher-badge">V2</span>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                    class="launcher-icon h-8 w-8 shrink-0" fill="currentColor">
                    <path
                        d="M12 2.75L13.817 8.183L19.25 10L13.817 11.817L12 17.25L10.183 11.817L4.75 10L10.183 8.183L12 2.75Z"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M18.5 15.5L19.232 17.518L21.25 18.25L19.232 18.982L18.5 21L17.768 18.982L15.75 18.25L17.768 17.518L18.5 15.5Z"
                        fill="currentColor" />
                </svg>
            </button>
        </transition>
    </div>
</template>

<script>
import { createAiChatController } from '@/vendor/ai-chat-opencode'
import { mapGetters, mapState } from 'vuex'

const STORAGE_KEY = 'mtw-opencode-demo-config'
const DEFAULT_SYSTEM = `{
  "PY-CRM-TOKEN": "LOGIN_TOKEN_demo",
  "PY-CRM-DEVICE": "music-tag-web"
}`

const DEFAULT_CONFIG = {
    opencodeUrl: 'http://127.0.0.1:9002',
    opencodeDirectory: '/home/opencode/app',
    opencodeSystem: DEFAULT_SYSTEM,
    opencodeAgent: 'build',
    opencodeModelID: 'deepseek-v4-pro',
    opencodeProviderID: 'deepseek',
    source: 'music-tag-web-demo',
    userId: ''
}

export default {
    name: 'FloatingAiChatLauncher',
    data() {
        return {
            showPanel: false,
            showSessions: false,
            panelConfig: Object.assign({}, DEFAULT_CONFIG),
            chat: this.createFallbackController(),
            input: '',
            panelReady: false,
            activeConfigSignature: '',
            expandedReasoningKeys: {},
            expandedProcessPartKeys: {},
            dragPosition: null,
            isDragging: false,
            dragMoved: false,
            dragOffset: { x: 0, y: 0 },
            dragStartPos: { x: 0, y: 0 },
            quickPrompts: [
                '怎么扫描导入本地音乐文件夹？',
                '如何批量修改歌曲的标签和封面？',
                '怎么设置后台自动刮削入库整理音乐？',
                '如何用手机 App 连接本系统播放音乐？'
            ]
        }
    },
    computed: {
        ...mapState({
            sysDict: state => state.common.sysDict || {}
        }),
        ...mapGetters(['getUsername', 'getUserRole']),
        wrapperStyle() {
            if (!this.showPanel && this.dragPosition) {
                return {
                    right: 'auto',
                    bottom: 'auto',
                    left: this.dragPosition.x + 'px',
                    top: this.dragPosition.y + 'px'
                }
            }
            return {
                right: '1rem',
                bottom: '1rem'
            }
        },
        launcherEnabled() {
            return this.normalizeBoolean(this.sysDict.opencode_show_launcher)
        },
        shouldShowLauncher() {
            return this.$route.name !== 'login' && this.launcherEnabled
        },
        isLauncherAvailable() {
            return this.getUserRole === 'V2'
        },
        launcherTitle() {
            return this.isLauncherAvailable ? '打开 AI 助手' : '智能助手是 V2 功能'
        },
        resolvedUserId() {
            if (this.getUsername) {
                return 'mtw_' + this.getUsername
            }
            if (typeof this.getCookie === 'function') {
                const cookieUser = this.getCookie('username')
                if (cookieUser) {
                    return 'mtw_' + cookieUser
                }
            }
            return 'mtw_demo_user'
        },
        effectiveUserId() {
            return this.panelConfig.userId || this.resolvedUserId
        },
        activeSessionLabel() {
            if (!this.chat.activeSessionId) {
                return '新草稿'
            }
            const current = this.chat.sessionList.find(item => item.id === this.chat.activeSessionId)
            return current ? this.chat.getSessionTitle(current) : this.chat.activeSessionId
        },
        backendPanelConfig() {
            return {
                opencodeUrl: this.sysDict.opencode_url,
                opencodeDirectory: this.sysDict.opencode_directory,
                opencodeSystem: this.sysDict.opencode_system,
                opencodeAgent: this.sysDict.opencode_agent,
                opencodeModelID: this.sysDict.opencode_model_id,
                opencodeProviderID: this.sysDict.opencode_provider_id,
                source: this.sysDict.opencode_source,
                userId: this.sysDict.opencode_user_id
            }
        }
    },
    watch: {
        'chat.messages.length'() {
            this.$nextTick(() => {
                this.scrollToBottom()
            })
        },
        'chat.isCurrentSessionLoading'() {
            this.$nextTick(() => {
                this.scrollToBottom()
            })
        },
        launcherEnabled(value) {
            if (!value) {
                this.showPanel = false
                this.showSessions = false
            }
        },
        isLauncherAvailable(value) {
            if (!value) {
                this.showPanel = false
                this.showSessions = false
            }
        }
    },
    mounted() {
        this.loadDragPosition()
        window.addEventListener('resize', this.handleViewportResize)
    },
    beforeDestroy() {
        this.disposeChat()
        this.removeDragListeners()
        window.removeEventListener('resize', this.handleViewportResize)
    },
    methods: {
        normalizeBoolean(value) {
            if (typeof value === 'boolean') {
                return value
            }
            if (typeof value === 'number') {
                return value === 1
            }
            if (typeof value === 'string') {
                return value === '1' || value.toLowerCase() === 'true'
            }
            return false
        },
        getDragBounds() {
            const launcherWidth = 80
            const launcherHeight = 100
            return {
                maxX: Math.max(0, window.innerWidth - launcherWidth),
                maxY: Math.max(0, window.innerHeight - launcherHeight)
            }
        },
        clampDragPosition(position) {
            if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
                return null
            }
            const { maxX, maxY } = this.getDragBounds()
            return {
                x: Math.max(0, Math.min(position.x, maxX)),
                y: Math.max(0, Math.min(position.y, maxY))
            }
        },
        loadDragPosition() {
            try {
                const saved = window.localStorage.getItem('mtw-ai-launcher-pos')
                if (saved) {
                    const pos = JSON.parse(saved)
                    const nextPosition = this.clampDragPosition(pos)
                    if (nextPosition) {
                        this.dragPosition = nextPosition
                        if (nextPosition.x !== pos.x || nextPosition.y !== pos.y) {
                            this.saveDragPosition()
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
        },
        saveDragPosition() {
            if (this.dragPosition) {
                try {
                    window.localStorage.setItem('mtw-ai-launcher-pos', JSON.stringify(this.dragPosition))
                } catch (e) {
                    // ignore
                }
            }
        },
        resetPosition() {
            this.dragPosition = null
            try {
                window.localStorage.removeItem('mtw-ai-launcher-pos')
            } catch (e) {
                // ignore
            }
        },
        handleViewportResize() {
            if (!this.dragPosition) {
                return
            }
            const nextPosition = this.clampDragPosition(this.dragPosition)
            if (!nextPosition) {
                this.resetPosition()
                return
            }
            if (nextPosition.x !== this.dragPosition.x || nextPosition.y !== this.dragPosition.y) {
                this.dragPosition = nextPosition
                this.saveDragPosition()
            }
        },
        onDragStart(event) {
            if (this.showPanel) return
            const clientX = event.touches ? event.touches[0].clientX : event.clientX
            const clientY = event.touches ? event.touches[0].clientY : event.clientY
            const rect = this.$el.getBoundingClientRect()
            this.dragOffset = {
                x: clientX - rect.left,
                y: clientY - rect.top
            }
            this.dragStartPos = { x: clientX, y: clientY }
            this.dragMoved = false
            this.isDragging = true
            document.addEventListener('mousemove', this.onDragMove)
            document.addEventListener('mouseup', this.onDragEnd)
            document.addEventListener('touchmove', this.onDragMove, { passive: false })
            document.addEventListener('touchend', this.onDragEnd)
            event.preventDefault()
        },
        onDragMove(event) {
            if (!this.isDragging) return
            const clientX = event.touches ? event.touches[0].clientX : event.clientX
            const clientY = event.touches ? event.touches[0].clientY : event.clientY
            const dx = clientX - this.dragStartPos.x
            const dy = clientY - this.dragStartPos.y
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                this.dragMoved = true
            }
            this.dragPosition = this.clampDragPosition({
                x: clientX - this.dragOffset.x,
                y: clientY - this.dragOffset.y
            })
            if (event.cancelable) event.preventDefault()
        },
        onDragEnd() {
            this.isDragging = false
            this.removeDragListeners()
            this.saveDragPosition()
            setTimeout(() => {
                this.dragMoved = false
            }, 50)
        },
        removeDragListeners() {
            document.removeEventListener('mousemove', this.onDragMove)
            document.removeEventListener('mouseup', this.onDragEnd)
            document.removeEventListener('touchmove', this.onDragMove)
            document.removeEventListener('touchend', this.onDragEnd)
        },
        createFallbackController() {
            return {
                sessionList: [],
                activeSessionId: null,
                messages: [],
                isCurrentSessionLoading: false,
                hasCurrentSessionReplyContent: false,
                getSessionTitle(session) {
                    return session && session.title ? session.title : '未命名会话'
                },
                isSessionGenerating() {
                    return false
                },
                renderMarkdownHtml(text) {
                    return String(text || '')
                }
            }
        },
        loadPanelConfig() {
            let localConfig = {}
            try {
                const raw = window.localStorage.getItem(STORAGE_KEY)
                if (raw) {
                    localConfig = JSON.parse(raw)
                }
            } catch (error) {
                console.warn('读取 AI 助手配置失败:', error)
            }
            this.panelConfig = Object.assign({}, DEFAULT_CONFIG, localConfig, this.getAvailableBackendConfig())
        },
        getAvailableBackendConfig() {
            const backendConfig = {}
            Object.keys(this.backendPanelConfig).forEach((key) => {
                const value = this.backendPanelConfig[key]
                if (value !== undefined && value !== null && String(value).trim() !== '') {
                    backendConfig[key] = value
                }
            })
            return backendConfig
        },
        buildConfigSignature(config) {
            return JSON.stringify({
                opencodeUrl: config.opencodeUrl,
                opencodeDirectory: config.opencodeDirectory,
                opencodeSystem: config.opencodeSystem,
                opencodeAgent: config.opencodeAgent,
                opencodeModelID: config.opencodeModelID,
                opencodeProviderID: config.opencodeProviderID,
                source: config.source,
                userId: this.effectiveUserId
            })
        },
        async ensureChatReady() {
            this.loadPanelConfig()
            const nextSignature = this.buildConfigSignature(this.panelConfig)
            if (this.panelReady && this.activeConfigSignature === nextSignature) {
                return
            }
            this.disposeChat()
            const controller = createAiChatController({
                opencodeUrl: this.panelConfig.opencodeUrl,
                opencodeDirectory: this.panelConfig.opencodeDirectory,
                opencodeSystem: this.panelConfig.opencodeSystem,
                opencodeAgent: this.panelConfig.opencodeAgent,
                opencodeModelID: this.panelConfig.opencodeModelID,
                opencodeProviderID: this.panelConfig.opencodeProviderID,
                source: this.panelConfig.source,
                userId: this.effectiveUserId,
                maxSessionCount: 10,
                initialMessages: [
                ],
                toast: this.customToast,
                confirm: this.customConfirm,
                onError: this.onControllerError
            })
            this.chat = controller
            await controller.init()
            this.panelReady = true
            this.activeConfigSignature = nextSignature
            this.$nextTick(() => {
                this.scrollToBottom()
            })
        },
        disposeChat() {
            if (this.chat && typeof this.chat.dispose === 'function') {
                this.chat.dispose()
            }
            this.panelReady = false
            this.activeConfigSignature = ''
        },
        async openPanel() {
            if (this.dragMoved) return
            if (!this.isLauncherAvailable) {
                this.customToast({
                    message: '智能助手仅对 V2 用户开放。',
                    type: 'warning'
                })
                return
            }
            this.showPanel = true
            try {
                await this.ensureChatReady()
                this.showSessions = false
            } catch (error) {
                this.showPanel = false
                this.customToast({
                    message: '初始化 AI 助手失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        closePanel() {
            this.showPanel = false
        },
        async sendMessage() {
            const text = this.input.trim()
            if (!text || !this.chat || this.chat.isCurrentSessionLoading) {
                return
            }
            if (!this.isLauncherAvailable) {
                this.customToast({
                    message: '智能助手仅对 V2 用户开放。',
                    type: 'warning'
                })
                return
            }
            try {
                await this.chat.sendMessage(text)
                this.input = ''
                this.$nextTick(() => {
                    this.scrollToBottom()
                })
            } catch (error) {
                this.customToast({
                    message: error && error.message ? error.message : '发送失败',
                    type: 'error'
                })
            }
        },
        applyQuickPrompt(prompt) {
            this.input = prompt
            this.$nextTick(() => {
                this.sendMessage()
            })
        },
        abort() {
            if (this.chat && typeof this.chat.abort === 'function') {
                this.chat.abort()
            }
        },
        async selectSession(sessionId) {
            if (!this.chat || !sessionId) {
                return
            }
            try {
                await this.chat.selectSession(sessionId)
                this.$nextTick(() => {
                    this.scrollToBottom()
                })
            } catch (error) {
                this.customToast({
                    message: '切换会话失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        async deleteSession(sessionId) {
            if (!this.chat || !sessionId) {
                return
            }
            try {
                await this.chat.deleteSession(sessionId)
            } catch (error) {
                this.customToast({
                    message: '删除会话失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        startNewDraft() {
            if (this.chat && typeof this.chat.startNewDraft === 'function') {
                this.chat.startNewDraft()
                this.showSessions = false
            }
        },
        async reloadMessages() {
            if (!this.chat || !this.chat.activeSessionId) {
                return
            }
            try {
                await this.chat.reloadMessages()
            } catch (error) {
                this.customToast({
                    message: '刷新历史失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        shortSessionId(value) {
            if (!value) {
                return ''
            }
            return String(value).slice(0, 6)
        },
        getReasoningKey(message, index) {
            return message.messageId || ('reasoning-' + index)
        },
        isReasoningExpanded(message, index) {
            if (this.isReasoningStreaming(message, index)) {
                return true
            }
            return Boolean(this.expandedReasoningKeys[this.getReasoningKey(message, index)])
        },
        isReasoningStreaming(message, index) {
            return Boolean(
                this.chat &&
                this.chat.isCurrentSessionLoading &&
                message &&
                message.role === 'assistant' &&
                message.reasoning &&
                !message.content &&
                index === this.chat.messages.length - 1
            )
        },
        hasProcessParts(message) {
            return Boolean(message && Array.isArray(message.processParts) && message.processParts.length)
        },
        shouldRenderProcessPart(part) {
            if (!part) {
                return false
            }
            const summary = this.getProcessPartSummary(part, false).trim()
            return Boolean(summary)
        },
        getVisibleProcessParts(message) {
            if (!this.hasProcessParts(message)) {
                return []
            }
            return message.processParts.filter((part) => this.shouldRenderProcessPart(part))
        },
        hasVisibleProcessParts(message) {
            return this.getVisibleProcessParts(message).length > 0
        },
        getProcessPartKey(message, index, part, partIndex) {
            const messageKey = this.getReasoningKey(message, index)
            return messageKey + ':process:' + (part && part.id ? part.id : partIndex)
        },
        isProcessPartExpanded(message, index, part, partIndex) {
            return Boolean(this.expandedProcessPartKeys[this.getProcessPartKey(message, index, part, partIndex)])
        },
        toggleProcessPart(message, index, part, partIndex) {
            const key = this.getProcessPartKey(message, index, part, partIndex)
            this.$set(this.expandedProcessPartKeys, key, !this.expandedProcessPartKeys[key])
        },
        getProcessPartLabel(part) {
            const type = part && part.type ? String(part.type) : 'unknown'
            const tool = part && part.tool ? String(part.tool) : ''
            const isChild = !!(part && part.sourceSessionId)
            if (type === 'tool') {
                return isChild
                    ? (tool ? '子任务工具: ' + tool : '子任务工具')
                    : (tool ? '工具调用: ' + tool : '工具调用')
            }
            if (type === 'step-start') {
                return isChild ? '子任务步骤开始' : '步骤开始'
            }
            if (type === 'step-finish') {
                return isChild ? '子任务步骤完成' : '步骤完成'
            }
            if (type === 'subsession-text') {
                return '子任务输出'
            }
            if (type === 'subsession-reasoning') {
                return '子任务思考'
            }
            if (type === 'subsession-status') {
                return '子任务状态'
            }
            return isChild ? '子任务事件: ' + type : type
        },
        getProcessPartStatus(part) {
            const status = part && part.status ? String(part.status) : ''
            if (!status) {
                return ''
            }
            if (status === 'running') return '进行中'
            if (status === 'completed') return '已完成'
            if (status === 'pending') return '等待中'
            if (status === 'failed') return '失败'
            return status
        },
        getProcessPartPreview(part) {
            if (!part) {
                return ''
            }
            const text = this.getProcessPartSummary(part, false).split('\n')[0] || ''
            return text || '查看详情'
        },
        getProcessPartSummary(part, withHeader = false) {
            if (!part) {
                return ''
            }
            const header = withHeader
                ? '[' + this.getProcessPartLabel(part) + (this.getProcessPartStatus(part) ? ' / ' + this.getProcessPartStatus(part) : '') + ']'
                : ''
            if (part.type === 'tool') {
                const state = part.state && typeof part.state === 'object' ? part.state : null
                const lines = []
                if (header) {
                    lines.push(header)
                }
                if (state && state.title) {
                    lines.push(state.title)
                }
                if (state && state.input && Object.keys(state.input).length) {
                    const inputSummary = state.input.description || state.input.title || ''
                    lines.push('input: ' + (inputSummary || JSON.stringify(state.input)))
                }
                if (state && state.output) {
                    lines.push('output: ' + String(state.output))
                }
                return lines.join('\n')
            }
            const lines = []
            if (header) {
                lines.push(header)
            }
            if (part.text) {
                lines.push(String(part.text))
                return lines.join('\n')
            }
            if (part.state && part.state.raw) {
                lines.push(String(part.state.raw))
                return lines.join('\n')
            }
            return lines.join('\n')
        },
        toggleReasoning(message, index) {
            const key = this.getReasoningKey(message, index)
            this.$set(this.expandedReasoningKeys, key, !this.expandedReasoningKeys[key])
        },
        scrollToBottom() {
            const scroller = this.$refs.messageScroller
            if (!scroller) {
                return
            }
            scroller.scrollTop = scroller.scrollHeight
        },
        customToast(payload) {
            const options = typeof payload === 'string' ? { message: payload } : (payload || {})
            const message = options.message || '收到通知'
            const type = ['success', 'error', 'warning'].includes(options.type) ? options.type : 'warning'
            if (this.$myMsg && this.$myMsg.notify) {
                this.$myMsg.notify({
                    title: 'AI 助手',
                    content: message,
                    type,
                    time: 2500
                })
                return
            }
            window.alert(message)
        },
        customConfirm(options) {
            const text = (options && options.title ? options.title + '\n' : '') + (options && options.message ? options.message : '确定继续吗？')
            return Promise.resolve(window.confirm(text))
        },
        onControllerError(error) {
            console.error('Floating AI chat error:', error)
        }
    }
}
</script>

<style scoped>
.floating-ai-chat-launcher {
    bottom: 1rem;
}

.launcher-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    cursor: grab;
    user-select: none;
}

.launcher-trigger:active {
    cursor: grabbing;
}

.launcher-trigger svg {
    display: block;
}

.launcher-icon {
    transform: translate(0px, 2.25px);
}

.launcher-badge {
    position: absolute;
    left: -0.25rem;
    top: -0.25rem;
    border-radius: 9999px;
    background: hsl(var(--wa));
    color: hsl(var(--wac));
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 0.2rem 0.35rem;
}

.prose :deep(pre) {
    white-space: pre-wrap;
    word-break: break-word;
}

.prose :deep(p:last-child) {
    margin-bottom: 0;
}

@media (min-width: 768px) {
    .floating-ai-chat-launcher {
        bottom: 1.25rem;
    }
}

@media (max-width: 768px) {
    .floating-ai-chat-launcher {
        bottom: calc(76px + env(safe-area-inset-bottom));
    }

    .floating-ai-chat-launcher section {
        width: calc(100vw - 1rem);
        height: calc(100vh - 6.75rem - env(safe-area-inset-bottom));
        max-height: calc(100vh - 6.75rem - env(safe-area-inset-bottom));
    }
}
</style>
